import { useParams, Link, useLocation } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/pages/home/components/Footer";
import { featureArticles } from "@/mocks/articles";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const article = featureArticles.find((a) => a.slug === slug);

  const siteUrl = import.meta.env.VITE_SITE_URL || "https://hipobuyspread.best";
  const currentUrl = `${siteUrl}${location.pathname}`;

  if (!article) {
    return (
      <main>
        <Navbar />
        <div className="pt-20 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center px-4">
            <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-4">
              Article Not Found
            </h1>
            <p className="text-foreground-600 mb-6">
              The article you are looking for does not exist yet.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-background-50 bg-primary-500 hover:bg-primary-600 rounded-full transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Homepage
            </Link>
          </div>
        </div>
        <Footer />
      </main>
    );
  }

  const otherArticles = featureArticles.filter((a) => a.slug !== article.slug);

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.excerpt,
    "image": article.image,
    "datePublished": article.date,
    "dateModified": article.date,
    "author": {
      "@type": "Organization",
      "name": "Hipobuy Spreadsheet",
      "url": siteUrl,
    },
    "publisher": {
      "@type": "Organization",
      "name": "Hipobuy Spreadsheet",
      "url": siteUrl,
      "logo": {
        "@type": "ImageObject",
        "url": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/16c9e81c106f6a445869e4a45760203f.jpeg",
      },
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": currentUrl,
    },
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": siteUrl,
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": article.title,
        "item": currentUrl,
      },
    ],
  };

  return (
    <main>
      <Navbar />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
      />

      <article className="pt-20">
        <header className="bg-background-100 py-14 md:py-20">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
            <p className="text-xs font-medium text-primary-600 uppercase tracking-widest mb-4">
              {article.category}
            </p>
            <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-4">
              {article.title}
            </h1>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto mb-6">
              {article.excerpt}
            </p>
            <div className="flex items-center justify-center gap-4 text-xs text-foreground-400">
              <span>Updated: {article.date}</span>
              <span>&middot;</span>
              <span>{article.readTime}</span>
            </div>
          </div>
        </header>

        <div className="max-w-3xl mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="prose-custom">
            <p className="text-base md:text-lg text-foreground-700 leading-relaxed mb-8">
              <span dangerouslySetInnerHTML={{ __html: article.content.intro }} />
            </p>

            <div className="my-10 p-6 bg-primary-50 rounded-2xl border border-primary-200/50">
              <p className="text-sm font-medium text-primary-700 mb-2">Quick Navigation</p>
              <p className="text-xs text-foreground-600">
                Looking for the main platform?{" "}
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-primary-600 font-medium hover:underline"
                >
                  Visit kaspreadsheet.com
                </a>{" "}
                to browse the complete Hipobuy Spreadsheet database with all product categories, comparison tools, and shopping guides.
              </p>
            </div>

            {article.content.sections.map((section, idx) => (
              <div key={idx}>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mt-10 mb-4">
                  {section.heading}
                </h2>
                {section.paragraphs.map((para, pIdx) => (
                  <p
                    key={pIdx}
                    className="text-base text-foreground-700 leading-relaxed mb-6"
                    dangerouslySetInnerHTML={{ __html: para }}
                  />
                ))}
              </div>
            ))}

            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mt-10 mb-4">
              Frequently Asked Questions
            </h2>

            {article.content.faqs.map((faq, idx) => (
              <div key={idx}>
                <h3 className="font-heading text-lg font-bold text-foreground-900 mt-6 mb-2">
                  {faq.q}
                </h3>
                <p className="text-base text-foreground-700 leading-relaxed mb-4">
                  {faq.a}
                </p>
              </div>
            ))}

            <div className="my-10 p-6 bg-accent-100/50 rounded-2xl border border-accent-200/50 text-center">
              <h3 className="font-heading text-lg font-bold text-foreground-950 mb-2">
                Ready to Start Exploring?
              </h3>
              <p className="text-sm text-foreground-600 mb-4">
                Access the complete Hipobuy Spreadsheet database and discover products across all categories.
              </p>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium text-background-50 bg-primary-500 hover:bg-primary-600 rounded-full transition-colors whitespace-nowrap cursor-pointer"
              >
                Go to Hipobuy Spreadsheet
                <i className="ri-arrow-right-up-line text-sm"></i>
              </a>
            </div>

            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mt-10 mb-4">
              Final Thoughts
            </h2>

            <p className="text-base text-foreground-700 leading-relaxed mb-8">
              {article.content.conclusion}
            </p>

            <p className="text-base text-foreground-700 leading-relaxed mb-8">
              To experience the full capabilities of Hipobuy Spreadsheet, visit the{" "}
              <Link to="/" className="text-primary-600 font-medium hover:underline">
                Hipobuy Spreadsheet homepage
              </Link>{" "}
              and start exploring the complete product database today. With organized categories, detailed product information, and a supportive community, you will wonder how you ever shopped without it.
            </p>
          </div>

          <div className="mt-12 pt-8 border-t border-background-200/70">
            <h3 className="font-heading text-lg font-bold text-foreground-950 mb-4">
              Related Articles
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {otherArticles.map((link) => (
                <Link
                  key={link.slug}
                  to={`/article/${link.slug}`}
                  className="p-4 bg-background-100 rounded-xl hover:bg-background-200/70 transition-colors group"
                >
                  <span className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors">
                    {link.title}
                  </span>
                  <i className="ri-arrow-right-line ml-2 text-xs text-foreground-400 group-hover:text-primary-600 transition-colors"></i>
                </Link>
              ))}
              <Link
                to="/"
                className="p-4 bg-background-100 rounded-xl hover:bg-background-200/70 transition-colors group"
              >
                <span className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors">
                  Back to Homepage
                </span>
                <i className="ri-arrow-right-line ml-2 text-xs text-foreground-400 group-hover:text-primary-600 transition-colors"></i>
              </Link>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </main>
  );
}